import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';

import cancelOrderReasons from 'common/constants/cancelOrderReasons';
import { cancelNonRoutableRequest } from 'store/thunks/workQueueThunks';

import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

// IT NO LONGER CLAIMS THE ROW LEAVES THE QUEUE, and that is not a softening. With Cancelled
// ticked in the Status filter the queue refresh brings the row straight back, so the old wording
// told the operator to expect something they would then watch not happen.
const CANCELLED =
  'Cancelled. This prescription will never be filled. The request drops out of the default view, ' +
  'and stays visible while the Status filter includes Cancelled.';

// POM-6279: the cancel mutation re-reads relayOrdersMayExist, so the operator is told at the moment
// of cancelling — not only from the row they opened, which may be stale.
const successMessage = (detail) =>
  detail?.relayOrdersMayExist
    ? {
        variant: 'warning',
        message: `${CANCELLED} Relay orders for this reference may exist and were NOT cancelled — check the Relay tab.`,
      }
    : CANCELLED;

// The cancel reasons the partner documentation lists as valid — the same list the backend accepts
// (NON_ROUTABLE_CANCEL_REASONS). NQ_REMOVED_BY_PARTNER is deliberately not offered. Labels come
// from the shared cancelOrderReasons constant.
const CANCEL_REASONS = [
  'PATIENT_REQUEST',
  'BILLING_REQUEST',
  'DOCTOR_REQUEST',
  'PATIENT_DECEASED',
  'NQ_DUPLICATE_ORDER',
  'NQ_TEST',
  'NQ_STATE',
];

/**
 * CANCEL A PARKED REQUEST (POM-6223).
 *
 * Terminal, and the opposite decision to Bring Back: the prescription is never filled and no POM
 * order is created. The partner IS told: the standard ORDER_CANCELED webhook goes out carrying the
 * reason the operator picks here, so a reason is required before Cancel is enabled.
 */
const CancelNonRoutableRequestDialog = ({ open, onClose, item }) => {
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: cancelNonRoutableRequest,
    successMessage,
    onClose,
    // Resets the outcome each time the dialog is opened. The row menu keeps both dialogs mounted,
    // so without this a previous attempt's alert would still be on screen with no confirm button.
    open,
  });

  const [cancelReason, setCancelReason] = useState('');

  // The row menu keeps the dialog mounted, so a previous pick must not carry over to the next open.
  useEffect(() => {
    if (open) setCancelReason('');
  }, [open]);

  // Every dismissal goes through `dismiss`, which refreshes the queue on the way out — the
  // refetch is deliberately NOT done when the mutation returns, because this dialog lives inside
  // the row it is resolving and the refresh would unmount it mid-message.
  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  // Same rule as Bring Back: after an attempt, only a backend-confirmed "nothing happened, the
  // row is back where it started" earns another click.
  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='CancelNonRoutableRequestDialog-title'
    >
      <DialogTitle id='CancelNonRoutableRequestDialog-title'>Cancel this request?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        <Alert severity='error'>
          <AlertTitle>This prescription will never be filled</AlertTitle>
          No order is created.
          <strong>
            {' '}
            The partner is sent a cancellation notice with the reason you choose.
          </strong>{' '}
          This cannot be undone.
        </Alert>

        <TextField
          select
          required
          fullWidth
          variant='standard'
          sx={{ mt: 2 }}
          id='CancelNonRoutableRequestDialog-cancelReason-dropdown'
          label='Reason for cancelling'
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
          disabled={submitting}
        >
          {CANCEL_REASONS.map((key) => (
            <MenuItem key={key} value={key}>
              {cancelOrderReasons[key]}
            </MenuItem>
          ))}
        </TextField>

        {/* POM-6279. Served from the relay orders themselves, never from the reason (a re-park can
            overwrite that). Cancelling this request ends the park only; it does not reach any
            relay order already written for the same reference. */}
        {item.relayOrdersMayExist && (
          <Alert
            severity='warning'
            sx={{ mt: 2 }}
            data-testid='non-routable-relay-orders-may-exist'
          >
            <AlertTitle>Relay orders may already exist for this reference</AlertTitle>
            Part of this order may already have been sent to a relay pharmacy. Cancelling this
            request does <strong>not</strong> cancel those relay orders — check the Relay tab before
            you cancel.
          </Alert>
        )}

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Keep the request'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='error'
            loading={submitting}
            disabled={!cancelReason}
            onClick={() => submit({ cancelReason })}
            id={`CancelNonRoutableRequestDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Cancel Request
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

CancelNonRoutableRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    status: PropTypes.string,
    relayOrdersMayExist: PropTypes.bool,
    source: PropTypes.string,
  }).isRequired,
};

export default CancelNonRoutableRequestDialog;
